/**
 * API response unwrapping helpers
 *
 * Cloudflare Workers list endpoints often return `data: { items, count }`
 * while Azure (and PaginatedResponse) use `data: T[]` with optional root `total`.
 * These helpers normalize both shapes so Redux always receives arrays.
 */

export type ListEnvelope = {
  success?: boolean;
  data?: unknown;
  total?: number;
  totalActive?: number;
  continuationToken?: string;
  userVotes?: Record<string, 'up' | 'down'>;
  pagination?: { hasMore?: boolean; continuationToken?: string };
  [key: string]: unknown;
};

export type UnwrappedListResponse<T> = {
  success: boolean;
  data: T[];
  total?: number;
  totalActive?: number;
  continuationToken?: string;
  userVotes?: Record<string, 'up' | 'down'>;
  [key: string]: unknown;
};

/**
 * Coerce any value to an array. Non-arrays become [].
 */
export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Extract a list array from common CF/Azure payload shapes.
 */
export function extractListItems<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) {
    return raw as T[];
  }

  if (!isRecord(raw)) {
    return [];
  }

  if (Array.isArray(raw.items)) {
    return raw.items as T[];
  }

  // Admin double-nest: data: { data: T[] }
  if (Array.isArray(raw.data)) {
    return raw.data as T[];
  }

  if (Array.isArray(raw.users)) {
    return raw.users as T[];
  }

  if (Array.isArray(raw.skus)) {
    return raw.skus as T[];
  }

  if (Array.isArray(raw.activities)) {
    return raw.activities as T[];
  }

  return [];
}

function extractTotal(
  envelope: ListEnvelope,
  rawData: unknown,
  items: unknown[]
): number | undefined {
  if (typeof envelope.total === 'number') {
    return envelope.total;
  }

  if (isRecord(rawData) && typeof rawData.count === 'number') {
    return rawData.count;
  }

  if (isRecord(rawData) && typeof rawData.total === 'number') {
    return rawData.total;
  }

  // Prefer explicit length only when CF gave us items without count
  if (isRecord(rawData) && Array.isArray(rawData.items)) {
    return items.length;
  }

  return undefined;
}

/**
 * Normalize a list API envelope to PaginatedResponse-compatible shape
 * with `data` always an array.
 */
export function unwrapListResponse<T>(envelope: ListEnvelope): UnwrappedListResponse<T> {
  if (!envelope || envelope.success === false) {
    throw new Error('Failed to fetch list');
  }

  const rawData = envelope.data;
  const items = extractListItems<T>(rawData);

  // Reject non-list object payloads that we could not unwrap (truthy but empty extract
  // only when the payload was clearly not a list container).
  if (
    rawData != null &&
    !Array.isArray(rawData) &&
    isRecord(rawData) &&
    items.length === 0 &&
    !('items' in rawData) &&
    !('data' in rawData) &&
    !('users' in rawData) &&
    !('skus' in rawData) &&
    !('activities' in rawData)
  ) {
    throw new Error('Failed to fetch list: unexpected response shape');
  }

  const total = extractTotal(envelope, rawData, items);
  const continuationToken =
    envelope.continuationToken ??
    (isRecord(rawData) && typeof rawData.continuationToken === 'string'
      ? rawData.continuationToken
      : undefined) ??
    envelope.pagination?.continuationToken;

  const result: UnwrappedListResponse<T> = {
    ...envelope,
    success: true,
    data: items,
  };

  if (total !== undefined) {
    result.total = total;
  }

  if (envelope.totalActive !== undefined) {
    result.totalActive = envelope.totalActive;
  } else if (isRecord(rawData) && typeof rawData.totalActive === 'number') {
    result.totalActive = rawData.totalActive;
  }

  if (continuationToken !== undefined) {
    result.continuationToken = continuationToken;
  }

  if (envelope.userVotes) {
    result.userVotes = envelope.userVotes;
  }

  return result;
}

/**
 * Unwrap a named collection inside `data` (e.g. `{ users }`, `{ skus }`).
 * Also accepts CF `{ items }` and Azure flat arrays.
 */
export function unwrapNamedList<T>(
  envelope: ListEnvelope,
  key: 'users' | 'skus' | 'activities' | 'items'
): T[] {
  if (!envelope || envelope.success === false) {
    return [];
  }

  const raw = envelope.data;
  if (Array.isArray(raw)) {
    return raw as T[];
  }

  if (isRecord(raw) && Array.isArray(raw[key])) {
    return raw[key] as T[];
  }

  return extractListItems<T>(raw);
}
